import React, { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import { FiCalendar, FiCheckCircle, FiDownload, FiMoreHorizontal, FiPhoneCall, FiRefreshCw, FiTrendingDown } from "react-icons/fi";
import { IoChevronDownOutline } from "react-icons/io5";
import { HiOutlineCurrencyDollar } from "react-icons/hi";
import useCallCenterT from "../../hooks/useCallCenterT";
import ReactSelector from "../../components/common/CommonReactSelector";
import { listWeeklyTrackerAgents, downloadWeeklyTrackerExcel, getWeeklyTracker } from "../../service/CallCenterAnalytics";

const DAY_LABELS = {
  SUNDAY: "Sunday",
  MONDAY: "Monday",
  TUESDAY: "Tuesday",
  WEDNESDAY: "Wednesday",
  THURSDAY: "Thursday",
};

const DAY_SHORT = {
  SUNDAY: "Sun",
  MONDAY: "Mon",
  TUESDAY: "Tue",
  WEDNESDAY: "Wed",
  THURSDAY: "Thu",
};

const toSundayIso = (dateStr) => {
  if (!dateStr) return moment().startOf("week").format("YYYY-MM-DD");
  return moment(dateStr).startOf("week").format("YYYY-MM-DD");
};

const formatOmr = (value) => {
  if (value == null || value === "") return "OMR 0";
  const raw = String(value);
  if (raw.toUpperCase().includes("OMR")) return raw;
  const num = Number(raw);
  if (Number.isNaN(num)) return raw;
  return `OMR ${num.toLocaleString()}`;
};

const statusTone = (name) => {
  const key = String(name || "").toUpperCase();
  if (key.includes("PAID") || key.includes("SUCCESS")) return "bg-emerald-50 text-emerald-700";
  if (key.includes("FAIL")) return "bg-red-50 text-red-600";
  if (key.includes("FOLLOW")) return "bg-orange-50 text-orange-600";
  if (key.includes("CALLBACK")) return "bg-sky-50 text-sky-700";
  if (key.includes("BUSY")) return "bg-violet-50 text-violet-700";
  return "bg-slate-100 text-slate-600";
};

const SemiGauge = ({ value, colorClass, label }) => {
  const size = 140;
  const stroke = 12;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const half = c / 2;
  const offset = half - (Math.max(0, Math.min(100, value)) / 100) * half;
  return (
    <div className="flex flex-col items-center">
      <div className="relative">
        <svg width={size} height={size / 2 + 10} viewBox={`0 0 ${size} ${size / 2 + 10}`} className={colorClass} aria-hidden>
          <path
            d={`M ${stroke / 2} ${size / 2} A ${r} ${r} 0 0 1 ${size - stroke / 2} ${size / 2}`}
            fill="none"
            className="stroke-slate-100"
            strokeWidth={stroke}
            strokeLinecap="round"
          />
          <path
            d={`M ${stroke / 2} ${size / 2} A ${r} ${r} 0 0 1 ${size - stroke / 2} ${size / 2}`}
            fill="none"
            stroke="currentColor"
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${half * 0.08} ${half * 0.06}`}
            strokeDashoffset={offset}
          />
        </svg>
        <span className="absolute inset-x-0 bottom-1 text-center text-lg font-bold text-slate-800">{value}%</span>
      </div>
      <p className="mt-1 text-xs font-medium text-slate-500">{label}</p>
    </div>
  );
};

const DailyBarChart = ({ items }) => {
  const max = Math.max(...items.map((item) => item.value), 1);
  const activeIndex = items.reduce((best, item, index) => (item.value > items[best].value ? index : best), 0);
  return (
    <div className="flex h-48 items-end gap-3">
      {items.map((item, index) => {
        const active = index === activeIndex && item.value > 0;
        const height = `${Math.max(8, (item.value / max) * 100)}%`;
        return (
          <div key={item.key} className="flex h-full min-w-0 flex-1 flex-col items-center justify-end">
            {active && (
              <span className="mb-1 text-xs font-semibold tabular-nums text-slate-700">{item.value}</span>
            )}
            <span
              className={`w-full max-w-[40px] rounded-t-xl ${active ? "bg-[#2B7FFF]" : "bg-slate-100"}`}
              style={{ height }}
            />
            <span className={`mt-2 text-[11px] ${active ? "font-semibold text-slate-700" : "text-slate-400"}`}>
              {item.label}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const StatCard = ({ title, value, footer, icon, trend, trendTone = "up" }) => (
  <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
    <div className="flex items-start justify-between gap-3">
      <p className="text-sm font-medium text-slate-500">{title}</p>
      <span className="text-[#2B7FFF]">{icon}</span>
    </div>
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <p className="text-[28px] font-bold leading-none tracking-tight text-slate-900 tabular-nums">{value}</p>
      {trend != null && (
        <span
          className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${
            trendTone === "down" ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-600"
          }`}
        >
          {trend}
        </span>
      )}
    </div>
    <p className="mt-2 text-xs text-slate-400">{footer}</p>
  </div>
);

const CallCenterWeeklyTrackerPage = () => {
  const t = useCallCenterT();
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(true);
  const [selectedAgentIds, setSelectedAgentIds] = useState([]);
  const [weekStart, setWeekStart] = useState(toSundayIso());
  const [tracker, setTracker] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setAgentsLoading(true);
        const users = await listWeeklyTrackerAgents();
        setAgents(Array.isArray(users) ? users : []);
      } catch (error) {
        toast.error(error.message || t("ccToastLoadOfficeUsersFailed"));
      } finally {
        setAgentsLoading(false);
      }
    })();
  }, [t]);

  const agentOptions = useMemo(
    () =>
      agents
        .filter((agent) => agent.userId != null)
        .map((agent) => ({
          value: Number(agent.userId),
          label: agent.name || agent.email || `${t("ccUserFallback")} ${agent.userId}`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [agents, t]
  );

  const selectedAgentOptions = useMemo(
    () => agentOptions.filter((option) => selectedAgentIds.includes(option.value)),
    [agentOptions, selectedAgentIds]
  );

  const loadTracker = useCallback(async () => {
    if (!selectedAgentIds.length) {
      setTracker(null);
      setHasLoadedOnce(false);
      return;
    }
    try {
      setLoading(true);
      const data = await getWeeklyTracker({
        weekStart,
        agentIds: selectedAgentIds,
        totalAgentCount: agents.length,
      });
      setTracker(data || null);
      setHasLoadedOnce(true);
    } catch (error) {
      setTracker(null);
      toast.error(error.message || t("ccWeeklyTrackerLoadFailed"));
    } finally {
      setLoading(false);
    }
  }, [selectedAgentIds, weekStart, agents.length, t]);

  useEffect(() => {
    if (!selectedAgentIds.length) {
      setTracker(null);
      setHasLoadedOnce(false);
      return undefined;
    }
    const timer = window.setTimeout(() => {
      loadTracker();
    }, 350);
    return () => window.clearTimeout(timer);
  }, [selectedAgentIds, weekStart, loadTracker]);

  const handleAgentChange = (selected) => {
    const next = (selected || [])
      .map((option) => Number(option.value))
      .filter((id) => Number.isFinite(id));
    setSelectedAgentIds(next);
  };

  const selectAllAgents = () => setSelectedAgentIds(agentOptions.map((option) => option.value));
  const clearAgents = () => {
    setSelectedAgentIds([]);
    setTracker(null);
    setHasLoadedOnce(false);
  };

  const handleExport = async () => {
    if (!selectedAgentIds.length) {
      toast.warn(t("ccWeeklyTrackerSelectAgents"));
      return;
    }
    try {
      setExporting(true);
      await downloadWeeklyTrackerExcel({
        weekStart,
        agentIds: selectedAgentIds,
        totalAgentCount: agents.length,
      });
      toast.success(t("ccWeeklyTrackerExportSuccess"));
    } catch (error) {
      toast.error(error.message || t("ccWeeklyTrackerExportFailed"));
    } finally {
      setExporting(false);
    }
  };

  const weekDays = useMemo(
    () => (Array.isArray(tracker?.weekDays) ? tracker.weekDays : Object.keys(DAY_LABELS)),
    [tracker]
  );

  const selectedCountLabel = t("ccWeeklyTrackerSelectedCount")
    .replace("{count}", String(selectedAgentIds.length))
    .replace("{total}", String(agents.length));

  const kpis = useMemo(() => {
    const agentRows = Array.isArray(tracker?.agents) ? tracker.agents : [];
    const calls = tracker?.managerSummary?.combinedTotalCalls ?? agentRows.reduce((sum, agent) => sum + Number(agent.weeklyCallTotal || 0), 0);
    const callTarget = tracker?.managerSummary?.combinedCallTarget ?? agentRows.reduce((sum, agent) => sum + Number(agent.weeklyCallTarget || 0), 0);
    const promise = agentRows.reduce((sum, agent) => sum + Number(agent.kpi?.promiseToPayAchieved || 0), 0);
    const collected = agentRows.reduce((sum, agent) => sum + Number(agent.kpi?.amountCollected || 0), 0);
    const missed = agentRows.reduce((sum, agent) => sum + Number(agent.kpi?.missedFollowUps || 0), 0);
    let followDone = 0;
    let followTarget = 0;
    agentRows.forEach((agent) => {
      (agent.statusRows || []).forEach((row) => {
        const name = String(row.statusName || "").toUpperCase();
        if (name.includes("FOLLOW")) {
          followDone += Number(row.weeklyTotal || 0);
          followTarget += Number(row.weeklyTarget || 0);
        }
      });
    });
    return { calls, callTarget, promise, collected, missed, followDone, followTarget };
  }, [tracker]);

  const dailyItems = useMemo(() => {
    const agentRows = Array.isArray(tracker?.agents) ? tracker.agents : [];
    return weekDays.map((day) => ({
      key: day,
      label: DAY_SHORT[day] || day.slice(0, 3),
      value: agentRows.reduce((sum, agent) => sum + Number(agent.dailyTotals?.[day] || 0), 0),
    }));
  }, [tracker, weekDays]);

  const callsCompletedPct =
    kpis.callTarget > 0 ? Math.min(100, Math.round((kpis.calls / kpis.callTarget) * 100)) : kpis.calls > 0 ? 100 : 0;
  const followupsPct =
    kpis.followTarget > 0 ? Math.min(100, Math.round((kpis.followDone / kpis.followTarget) * 100)) : kpis.followDone > 0 ? 100 : 0;
  const timezone = tracker?.timezone || "Asia/Muscat";

  return (
    <div className="min-h-screen bg-[#f4f7fb]">
      <div className="mx-auto  px-4 py-6 ">
        <header className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900">{t("ccWeeklyTrackerTitle")}</h1>
            <p className="mt-1 max-w-2xl text-sm text-slate-500">{t("ccWeeklyTrackerSubtitle")}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex h-10 items-center overflow-hidden rounded-full border border-slate-200 bg-white text-sm font-medium text-slate-700">
              <label className="relative inline-flex h-full cursor-pointer items-center gap-2 px-4">
                <FiCalendar className="h-4 w-4 text-slate-500" />
                <span>Week of {moment(weekStart).format("D MMM YYYY")}</span>
                <input
                  type="date"
                  value={weekStart}
                  onChange={(e) => setWeekStart(toSundayIso(e.target.value))}
                  className="absolute inset-0 cursor-pointer opacity-0"
                  aria-label={t("ccWeeklyTrackerWeekOf")}
                />
              </label>
              <span className="h-5 w-px bg-slate-200" />
              <span className="inline-flex h-full items-center gap-1.5 px-4">
                {timezone}
                <IoChevronDownOutline className="h-3.5 w-3.5 text-slate-400" />
              </span>
            </div>
            <button
              type="button"
              onClick={loadTracker}
              disabled={loading || agentsLoading}
              className="inline-flex h-10 items-center gap-2 rounded-full border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-60"
            >
              <FiRefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              {loading ? t("ccRefreshing") : t("ccRefreshAnalytics")}
            </button>
            <button
              type="button"
              onClick={handleExport}
              disabled={exporting || loading || agentsLoading}
              className="inline-flex h-10 items-center gap-2 rounded-full bg-[#2B7FFF] px-5 text-sm font-medium text-white transition hover:bg-[#236DB4] disabled:opacity-60"
            >
              <FiDownload className="h-4 w-4" />
              {exporting ? t("ccWeeklyTrackerExporting") : t("ccWeeklyTrackerDownloadExcel")}
            </button>
          </div>
        </header>

        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            title={t("ccWeeklyTrackerCallsThisWeek")}
            value={kpis.calls}
            footer={kpis.callTarget ? `vs. ${kpis.callTarget} target` : t("ccWeeklyTrackerCallsThisWeek")}
            icon={<FiPhoneCall className="h-5 w-5" />}
          />
          <StatCard
            title={t("ccWeeklyTrackerKpiPromise")}
            value={kpis.promise}
            footer={t("ccWeeklyTrackerCommitments")}
            icon={<FiCheckCircle className="h-5 w-5" />}
          />
          <StatCard
            title={t("ccWeeklyTrackerKpiCollected")}
            value={formatOmr(kpis.collected)}
            footer={t("ccWeeklyTrackerRecoveredThisWeek")}
            icon={<HiOutlineCurrencyDollar className="h-5 w-5" />}
          />
          <StatCard
            title={t("ccWeeklyTrackerKpiMissed")}
            value={kpis.missed}
            footer={t("ccWeeklyTrackerNeedsAttention")}
            icon={<FiTrendingDown className="h-5 w-5" />}
            trend={kpis.missed > 0 ? `↓ ${kpis.missed}` : null}
            trendTone="down"
          />
        </div>

        <section className="mb-6 rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold text-slate-800">{t("ccWeeklyTrackerAgents")}</h2>
            <div className="flex items-center gap-3 text-xs font-semibold">
              <button
                type="button"
                onClick={selectAllAgents}
                disabled={agentsLoading || !agentOptions.length}
                className="text-[#2B7FFF] hover:underline disabled:opacity-50"
              >
                {t("ccWeeklyTrackerSelectAll")}
              </button>
              <button
                type="button"
                onClick={clearAgents}
                disabled={agentsLoading || !selectedAgentIds.length}
                className="text-slate-500 hover:underline disabled:opacity-50"
              >
                {t("ccWeeklyTrackerClearAll")}
              </button>
            </div>
          </div>
          <div className="multiple-activity-select-scroll">
            <ReactSelector
              isMulti
              isSearchable
              isClearable
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              isDisabled={agentsLoading}
              options={agentOptions}
              value={selectedAgentOptions}
              onChange={handleAgentChange}
              placeholder={agentsLoading ? t("ccLoading") : t("ccWeeklyTrackerSearchAgents")}
              height="auto"
              minHeight="42px"
              fontSize="14px"
              maxMenuHeight="280px"
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: "44px",
                  height: "auto",
                  borderRadius: "12px",
                  borderColor: "#e2e8f0",
                  boxShadow: "none",
                  alignItems: "flex-start",
                }),
                multiValue: (base) => ({
                  ...base,
                  backgroundColor: "#DBEAFE",
                  borderRadius: "999px",
                }),
                multiValueLabel: (base) => ({
                  ...base,
                  color: "#1D4ED8",
                  fontWeight: 600,
                }),
                valueContainer: (base) => ({
                  ...base,
                  maxHeight: "88px",
                  overflowY: "auto",
                  padding: "6px 10px",
                }),
              }}
            />
          </div>
          <p className="mt-2 text-xs text-slate-400">
            {agentsLoading ? t("ccLoading") : selectedCountLabel}
            {loading && selectedAgentIds.length > 0 ? ` · ${t("ccRefreshing")}` : ""}
          </p>
        </section>

        {loading && selectedAgentIds.length > 0 && (
          <div className="mb-6 rounded-2xl border border-slate-200/80 bg-white py-16 text-center text-sm text-slate-400">
            {t("ccRefreshing")}
          </div>
        )}

        {!loading && !selectedAgentIds.length && (
          <div className="mb-6 rounded-2xl border border-dashed border-slate-200 bg-white py-16 text-center text-sm text-slate-400">
            {t("ccWeeklyTrackerSelectAgents")}
          </div>
        )}

        {!loading && Array.isArray(tracker?.agents) && tracker.agents.length > 0 ? (
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
            <div className="space-y-6 xl:col-span-7">
              {tracker.agents.map((agent) => (
                <section key={agent.agentId} className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
                  <div className="mb-4 flex items-start justify-between gap-3">
                    <div>
                      <h2 className="text-base font-semibold text-slate-800">{agent.agentName}</h2>
                      <p className="text-xs text-slate-400">
                        {t("ccWeeklyTrackerSubtitle")}
                      </p>
                    </div>
                    <button type="button" className="rounded-lg p-1 text-slate-400 hover:bg-slate-50" aria-label="More">
                      <FiMoreHorizontal className="h-5 w-5" />
                    </button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[640px] text-sm">
                      <thead>
                        <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                          <th className="pb-3 pr-3">{t("ccWeeklyTrackerCallType")}</th>
                          {weekDays.map((day) => (
                            <th key={day} className="pb-3 pr-3 text-center">
                              {DAY_SHORT[day] || day}
                            </th>
                          ))}
                          <th className="pb-3 text-center">{t("ccWeeklyTrackerWeeklyTotal")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(agent.statusRows || []).map((row) => (
                          <tr key={row.statusId} className="border-t border-slate-100">
                            <td className="py-3 pr-3">
                              <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${statusTone(row.statusName)}`}>
                                {row.statusName}
                              </span>
                            </td>
                            {weekDays.map((day) => (
                              <td key={day} className="py-3 pr-3 text-center tabular-nums text-slate-700">
                                {row.dailyCounts?.[day] ?? 0}
                              </td>
                            ))}
                            <td className="py-3 text-center font-semibold tabular-nums text-slate-800">
                              {row.weeklyTotal ?? 0}
                            </td>
                          </tr>
                        ))}
                        <tr className="border-t border-slate-200 font-semibold text-slate-800">
                          <td className="py-3 pr-3">{t("ccTotalCalls")}</td>
                          {weekDays.map((day) => (
                            <td key={day} className="py-3 pr-3 text-center tabular-nums">
                              {agent.dailyTotals?.[day] ?? 0}
                            </td>
                          ))}
                          <td className="py-3 text-center tabular-nums">{agent.weeklyCallTotal ?? 0}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </section>
              ))}
            </div>

            <aside className="space-y-6 xl:col-span-5">
              <section className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
                <h3 className="mb-4 text-base font-semibold text-slate-800">{t("ccWeeklyTrackerDailyCalls")}</h3>
                <DailyBarChart items={dailyItems} />
              </section>
              <section className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
                <div className="grid grid-cols-2 gap-3">
                  <SemiGauge
                    value={callsCompletedPct}
                    colorClass="text-[#2B7FFF]"
                    label={`${callsCompletedPct}% ${t("ccWeeklyTrackerCallsCompleted")}`}
                  />
                  <SemiGauge
                    value={followupsPct}
                    colorClass="text-emerald-500"
                    label={`${followupsPct}% ${t("ccWeeklyTrackerFollowupsDone")}`}
                  />
                </div>
                <p className="mt-4 text-center text-xs text-slate-400">
                  {t("ccWeeklyTrackerTargetCallsPlaced")
                    .replace("{done}", String(kpis.calls))
                    .replace("{target}", String(kpis.callTarget || 0))}
                </p>
              </section>
            </aside>
          </div>
        ) : (
          !loading &&
          hasLoadedOnce && (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-white py-16 text-center text-sm text-slate-400">
              {t("ccWeeklyTrackerNoData")}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default CallCenterWeeklyTrackerPage;
